/**
 * Upload Service for TheAdsFather Phase 2 Frontend
 * Handles file uploads to S3 using presigned URLs and manages upload progress
 */

import { apiService } from './api';
import { AdMetadata, AdRecord } from '../types';

export const HTML_UPLOAD_RATIO = '16:9';
export const SUPPORTED_UPLOAD_EXTENSIONS = [
  '.mp4',
  '.mov',
  '.avi',
  '.mkv',
  '.webm',
  '.jpg',
  '.jpeg',
  '.png',
  '.html',
  '.htm',
] as const;
export const SUPPORTED_UPLOAD_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/webm',
  'image/jpeg',
  'image/png',
  'text/html',
  'application/xhtml+xml',
] as const;
export const DROPZONE_ACCEPT: Record<string, string[]> = {
  'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
  'image/*': ['.jpg', '.jpeg', '.png'],
  'text/html': ['.html', '.htm'],
};

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
  '.webm': 'video/webm',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.html': 'text/html',
  '.htm': 'text/html',
};
const IMAGE_UPLOAD_EXTENSIONS = ['.jpg', '.jpeg', '.png'] as const;


export const HTML_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

type HtmlPlatformValidation = {
  platform: string;
  isValid: boolean;
  required: string;
};

type HtmlValidationResult =
  | { isValid: true; platformValidations: HtmlPlatformValidation[] }
  | { isValid: false; error: string; platformValidations?: HtmlPlatformValidation[] };

export const validateHtmlPlayableContent = (_html: string, _platforms: readonly string[] = []): HtmlValidationResult => ({
  isValid: true,
  platformValidations: [],
});
type MediaMetadata = {
  duration: number;
  width: number;
  height: number;
  aspectRatio: string;
};

const HTML_RATIO_META_NAMES = new Set([
  'adfather:ratio',
  'creative:ratio',
  'aspect-ratio',
  'aspect_ratio',
  'ratio',
]);

const calculateAspectRatio = (width: number, height: number): MediaMetadata => {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const roundedWidth = Math.round(width);
  const roundedHeight = Math.round(height);
  const divisor = gcd(roundedWidth, roundedHeight) || 1;

  return {
    duration: 0,
    width: roundedWidth,
    height: roundedHeight,
    aspectRatio: `${Math.round(roundedWidth / divisor)}:${Math.round(roundedHeight / divisor)}`,
  };
};

const parseRatioValue = (value: string): MediaMetadata | null => {
  const ratioMatch = value.match(/(?:^|[^\d])(\d{1,5})\s*[:x/]\s*(\d{1,5})(?:[^\d]|$)/i);
  if (!ratioMatch) {
    return null;
  }

  const width = Number(ratioMatch[1]);
  const height = Number(ratioMatch[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return calculateAspectRatio(width, height);
};

const parseDimensionDeclaration = (value: string): MediaMetadata | null => {
  const widthMatch = value.match(/\bwidth\s*=\s*(\d{1,5})\b/i);
  const heightMatch = value.match(/\bheight\s*=\s*(\d{1,5})\b/i);
  if (!widthMatch || !heightMatch) {
    return null;
  }

  const width = Number(widthMatch[1]);
  const height = Number(heightMatch[1]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  return calculateAspectRatio(width, height);
};

const readFileText = (file: File): Promise<string> => {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read HTML file'));
    reader.readAsText(file);
  });
};


const extractHtmlMediaMetadata = async (file: File): Promise<MediaMetadata> => {
  const html = await readFileText(file);
  const documentElement = new DOMParser().parseFromString(html, 'text/html');
  const metas = Array.from(documentElement.querySelectorAll('meta'));

  for (const meta of metas) {
    const key = (meta.getAttribute('name') || meta.getAttribute('property') || meta.getAttribute('http-equiv') || '').toLowerCase();
    const content = meta.getAttribute('content') || '';

    if (key === 'ad.size' || key === 'ad-size' || key === 'viewport') {
      const dimensions = parseDimensionDeclaration(content);
      if (dimensions) {
        return dimensions;
      }
    }

    if (HTML_RATIO_META_NAMES.has(key)) {
      const ratio = parseRatioValue(content);
      if (ratio) {
        return ratio;
      }
    }
  }

  throw new Error('Unable to determine HTML creative ratio. Add <meta name="ad.size" content="width=1080,height=1920"> or <meta name="aspect-ratio" content="9:16"> to the HTML file.');
};

const getFileExtension = (filename: string): string => {
  const extensionIndex = filename.lastIndexOf('.');
  if (extensionIndex < 0) {
    return '';
  }

  return filename.toLowerCase().slice(extensionIndex);
};

export const isHtmlUploadFile = (file: Pick<File, 'name' | 'type'>, filenameOverride?: string): boolean => {
  const filename = filenameOverride || file.name || '';
  const extension = getFileExtension(filename);

  return (file.type || '').toLowerCase().startsWith('text/html') || extension === '.html' || extension === '.htm';
};

const isImageUploadFile = (file: Pick<File, 'name' | 'type'>, filenameOverride?: string): boolean => {
  const filename = filenameOverride || file.name || '';
  const extension = getFileExtension(filename);
  const imageExtensions: readonly string[] = IMAGE_UPLOAD_EXTENSIONS;

  return (file.type || '').toLowerCase().startsWith('image/') || imageExtensions.includes(extension);
};

const getUploadContentType = (file: Pick<File, 'name' | 'type'>, filename: string): string => (
  file.type || CONTENT_TYPE_BY_EXTENSION[getFileExtension(filename)] || 'application/octet-stream'
);

// Types
export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  success: boolean;
  adRecord?: AdRecord;
  error?: string;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  onStart?: () => void;
  onComplete?: (adRecord: AdRecord) => void;
  onError?: (error: string) => void;
  timeout?: number;
}

/**
 * Upload Service Class
 */
export class UploadService {
  private activeUploads: Map<string, XMLHttpRequest> = new Map();

  /**
   * Upload a file to S3 using presigned URL
   */
  async uploadFile(file: File, metadata: AdMetadata, options: UploadOptions = {}): Promise<UploadResult> {
    const { onProgress, onStart, onComplete, onError, timeout = 300000 } = options; // 5 minutes default timeout
    
    try {
      // Validate file using metadata filename instead of file.name (which might be undefined)
      const validation = this.validateFileWithMetadata(file, metadata);
      if (!validation.isValid) {
        const error = `File validation failed: ${validation.error}`;
        onError?.(error);
        return { success: false, error };
      }

      // Start upload process
      onStart?.();

      // Use user-provided filename from metadata, fallback to file.name if needed
      const filename = metadata.filename || metadata.name_en || file.name || 'unnamed_file';
      const contentType = getUploadContentType(file, filename);
      // HTML playables are only checked for file size locally. Platform-specific
      // creative validation happens on upload to the ad network; local static
      // checks were rejecting creatives that those platforms already accept.

      
      // Step 1: Initiate upload to get presigned URL
      console.log('Initiating upload for file:', filename);
      const uploadSession = await apiService.initiateUpload({
        ...metadata,
        filename: filename,
        content_type: contentType,
      });

      console.log('Upload session created:', uploadSession.session_id);

      // Step 2: Upload file to S3
      const uploadSuccess = await this.uploadToS3(
        file,
        uploadSession.presigned_url,
        (progress) => onProgress?.(progress),
        timeout,
        contentType
      );

      if (!uploadSuccess) {
        const error = 'Failed to upload file to S3';
        onError?.(error);
        return { success: false, error };
      }

      console.log('File uploaded to S3 successfully');

      // Step 3: Determine duration/ratio depending on media type
      let finalMetadata = { ...metadata };
      if (isHtmlUploadFile(file, filename)) {
        finalMetadata.duration = 0;
        if (finalMetadata.filename && finalMetadata.filename.includes('_TBDs')) {
          finalMetadata.filename = finalMetadata.filename.replace('_TBDs', `_0s`);
        }
      } else if (isImageUploadFile(file, filename)) {
        // For images, duration is always 0s
        finalMetadata.duration = 0;
        // If filename used TBDs, update to 0s for consistency
        if (finalMetadata.filename && finalMetadata.filename.includes('_TBDs')) {
          finalMetadata.filename = finalMetadata.filename.replace('_TBDs', `_0s`);
        }
      } else {
        // Video flow: duration is mandatory; auto-extract if not provided
        if (!finalMetadata.duration) {
          try {
            console.log('Extracting video metadata for duration...');
            const videoMetadata = await this.extractVideoMetadata(file);
            // Always round up duration to the next whole second
            finalMetadata.duration = Math.ceil(videoMetadata.duration);
            console.log('Extracted duration:', videoMetadata.duration, 'seconds');
            
            // Update filename to include actual duration if it was previously TBDs
            if (finalMetadata.filename && finalMetadata.filename.includes('_TBDs')) {
              const actualDurationPart = `${Math.ceil(videoMetadata.duration)}s`;
              finalMetadata.filename = finalMetadata.filename.replace('_TBDs', `_${actualDurationPart}`);
              console.log('Updated filename with actual duration:', finalMetadata.filename);
            }
          } catch (error) {
            console.error('Failed to extract video duration - upload cannot proceed:', error);
            throw new Error('Unable to determine video duration. Please ensure the video file is valid and try again.');
          }
        }

        // Ensure any provided duration is rounded up as well
        if (typeof finalMetadata.duration === 'number') {
          finalMetadata.duration = Math.ceil(finalMetadata.duration);
        }

        // Final validation for videos - duration must be > 0
        if (!finalMetadata.duration || finalMetadata.duration <= 0) {
          throw new Error('Video duration could not be determined. Please ensure the video file is valid.');
        }
      }

      // Step 4: Complete upload process
      const adRecord = await apiService.completeUpload(
        uploadSession.session_id,
        uploadSession.s3_key,
        finalMetadata
      );

      console.log('Upload completed, ad record created:', adRecord.ad_id);

      // Notify completion
      onComplete?.(adRecord);

      return { success: true, adRecord };

    } catch (error: any) {
      const errorMessage = error.message || 'Upload failed';
      console.error('Upload error:', errorMessage);
      onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  private uploadToS3(
    file: File,
    presignedUrl: string,
    onProgress?: (progress: UploadProgress) => void,
    timeout: number = 300000,
    contentType?: string
  ): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const uploadId = `upload_${Date.now()}_${Math.random()}`;

    // Store reference for potential cancellation
    this.activeUploads.set(uploadId, xhr);

    // Set timeout
    xhr.timeout = timeout;

    // Progress tracking
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress: UploadProgress = {
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100)
        };
        onProgress?.(progress);

        // Log progress for debugging
        console.log(`Upload progress: ${progress.percentage}% (${progress.loaded}/${progress.total} bytes)`);
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      this.activeUploads.delete(uploadId);

      console.log(`S3 upload completed with status: ${xhr.status}`);
      console.log(`Response headers:`, xhr.getAllResponseHeaders());

      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('S3 upload successful');
        resolve(true);
      } else {
        console.error('S3 upload failed with status:', xhr.status);
        console.error('Response text:', xhr.responseText);
        reject(new Error(`S3 upload failed with status ${xhr.status}: ${xhr.responseText}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      this.activeUploads.delete(uploadId);
      console.error('S3 upload network error');
      console.error('XHR status:', xhr.status, 'Ready state:', xhr.readyState);
      reject(new Error('S3 upload failed due to network error'));
    });

    // Handle timeout
    xhr.addEventListener('timeout', () => {
      this.activeUploads.delete(uploadId);
      console.error('S3 upload timeout after', timeout, 'ms');
      reject(new Error(`S3 upload timeout after ${timeout/1000} seconds`));
    });

    // Handle abort
    xhr.addEventListener('abort', () => {
      this.activeUploads.delete(uploadId);
      console.log('S3 upload aborted');
      reject(new Error('S3 upload aborted'));
    });

    // Add load start event for debugging
    xhr.addEventListener('loadstart', () => {
      console.log(`Starting S3 upload for file: ${file.name} (${file.size} bytes)`);
      console.log(`Upload URL: ${presignedUrl.substring(0, 100)}...`);
    });

    // Start upload
    try {
      xhr.open('PUT', presignedUrl);
      const requestContentType = contentType || file.type || 'application/octet-stream';
      xhr.setRequestHeader('Content-Type', requestContentType);
      console.log(`Sending file with Content-Type: ${requestContentType}`);
      xhr.send(file);
    } catch (error) {
      this.activeUploads.delete(uploadId);
      console.error('Error starting S3 upload:', error);
      reject(new Error(`Failed to start S3 upload: ${error}`));
    }
    });
  }

  /**
   * Cancel an active upload
   */
  cancelUpload(uploadId?: string): void {
    if (uploadId && this.activeUploads.has(uploadId)) {
      const xhr = this.activeUploads.get(uploadId);
      xhr?.abort();
      this.activeUploads.delete(uploadId);
    } else {
      // Cancel all active uploads
      this.activeUploads.forEach((xhr) => xhr.abort());
      this.activeUploads.clear();
    }
  }

  /**
   * Get active upload count
   */
  getActiveUploadCount(): number {
    return this.activeUploads.size;
  }

  /**
   * Validate file with metadata (uses metadata filename instead of file.name)
   */
  validateFileWithMetadata(file: File, metadata: AdMetadata): { isValid: boolean; error?: string } {
    // Check if file object is valid
    if (!file) {
      return {
        isValid: false,
        error: 'No file provided'
      };
    }

    // Use filename from metadata instead of file.name
    const filename = metadata.filename || metadata.name_en;
    if (!filename || filename.trim() === '') {
      return {
        isValid: false,
        error: 'File name is required in metadata'
      };
    }

    const maxSize = isHtmlUploadFile(file, filename) ? HTML_MAX_FILE_SIZE_BYTES : 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size ${this.formatFileSize(file.size)} exceeds maximum allowed size of ${this.formatFileSize(maxSize)}`
      };
    }

    // Check file type
    const allowedTypes: readonly string[] = SUPPORTED_UPLOAD_MIME_TYPES;
    const allowedExtensions: readonly string[] = SUPPORTED_UPLOAD_EXTENSIONS;
    const fileExtension = getFileExtension(filename);
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File type not supported. Allowed types: ${allowedExtensions.join(', ')}`
      };
    }

    return { isValid: true };
  }

  /**
   * Validate file before upload (legacy method - validates using file.name)
   */
  validateFile(file: File): { isValid: boolean; error?: string } {
    // Check if file object is valid
    if (!file) {
      return {
        isValid: false,
        error: 'No file provided'
      };
    }

    // Check file name first (before using it)
    if (!file.name || file.name.trim() === '') {
      return {
        isValid: false,
        error: 'File name is required'
      };
    }

    const maxSize = isHtmlUploadFile(file) ? HTML_MAX_FILE_SIZE_BYTES : 2 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `File size ${this.formatFileSize(file.size)} exceeds maximum allowed size of ${this.formatFileSize(maxSize)}`
      };
    }

    // Check file type
    const allowedTypes: readonly string[] = SUPPORTED_UPLOAD_MIME_TYPES;
    const allowedExtensions: readonly string[] = SUPPORTED_UPLOAD_EXTENSIONS;
    const fileExtension = getFileExtension(file.name);
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File type not supported. Allowed types: ${allowedExtensions.join(', ')}`
      };
    }

    return { isValid: true };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Estimate upload time based on file size and connection speed
   */
  estimateUploadTime(fileSize: number, connectionSpeedMbps: number = 10): string {
    const fileSizeMb = fileSize / (1024 * 1024);
    const timeSeconds = fileSizeMb / connectionSpeedMbps;
    
    if (timeSeconds < 60) {
      return `${Math.round(timeSeconds)} seconds`;
    } else if (timeSeconds < 3600) {
      return `${Math.round(timeSeconds / 60)} minutes`;
    } else {
      return `${Math.round(timeSeconds / 3600)} hours`;
    }
  }

  /**
   * Check if file type is supported
   */
  isFileTypeSupported(file: File): boolean {
    const validation = this.validateFile(file);
    return validation.isValid;
  }

  /**
   * Get supported file types for display
   */
  getSupportedFileTypes(): string[] {
    return Array.from(SUPPORTED_UPLOAD_EXTENSIONS);
  }

  /**
   * Create a preview URL for video file
   */
  createPreviewUrl(file: File): string | null {
    if (this.isFileTypeSupported(file)) {
      return URL.createObjectURL(file);
    }
    return null;
  }

  /**
   * Revoke preview URL to free memory
   */
  revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Extract basic video metadata (duration, dimensions) from file
   */
  extractVideoMetadata(file: File): Promise<{
    duration: number;
    width: number;
    height: number;
    aspectRatio: string;
  }> {
    return new Promise<MediaMetadata>((resolve, reject) => {
      const video = document.createElement('video');
      const url = this.createPreviewUrl(file);

      if (!url) {
        reject(new Error('Unsupported file type'));
        return;
      }

    video.addEventListener('loadedmetadata', () => {
      const duration = Math.ceil(video.duration); // Always round up to nearest second
      const width = video.videoWidth;
      const height = video.videoHeight;

      // Calculate aspect ratio
      const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
      const divisor = gcd(width, height);
      const aspectRatio = `${width / divisor}:${height / divisor}`;

      this.revokePreviewUrl(url);

      resolve({
        duration,
        width,
        height,
        aspectRatio
      });
    });

    video.addEventListener('error', () => {
      this.revokePreviewUrl(url);
      reject(new Error('Failed to load video metadata'));
    });

    video.src = url;
    });
  }

  /**
   * Extract media metadata for video, image, or HTML files
   */
  extractMediaMetadata(file: File): Promise<MediaMetadata> {
    if (isHtmlUploadFile(file)) {
      const filenameMetadata = parseRatioValue(file.name || '');
      if (filenameMetadata) {
        return Promise.resolve(filenameMetadata);
      }

      return extractHtmlMediaMetadata(file).catch(() => ({
        duration: 0,
        width: 16,
        height: 9,
        aspectRatio: HTML_UPLOAD_RATIO,
      }));
    }
    if (isImageUploadFile(file)) {
      return new Promise<MediaMetadata>((resolve, reject) => {
        const img = new Image();
        const url = this.createPreviewUrl(file);
        if (!url) {
          reject(new Error('Unsupported file type'));
          return;
        }
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
        const divisor = gcd(width, height) || 1;
        const aspectRatio = `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
        this.revokePreviewUrl(url);
        resolve({ duration: 0, width, height, aspectRatio });
      };
      img.onerror = () => {
        this.revokePreviewUrl(url);
        reject(new Error('Failed to load image metadata'));
      };
      img.src = url;
      });
    }
    // Fallback to video extraction for non-image files
    return this.extractVideoMetadata(file);
  }

  /**
   * Batch upload multiple files
   */
  async uploadMultipleFiles(
    files: File[],
    metadataList: AdMetadata[],
    options: UploadOptions = {}
  ): Promise<UploadResult[]> {
    if (files.length !== metadataList.length) {
      throw new Error('Files and metadata arrays must have the same length');
    }

    const results: UploadResult[] = [];
    
    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const metadata = metadataList[i];
      
      console.log(`Uploading file ${i + 1} of ${files.length}: ${file.name}`);
      
      const result = await this.uploadFile(file, metadata, {
        ...options,
        onProgress: (progress) => {
          options.onProgress?.(progress);
        }
      });
      
      results.push(result);
      
      // If upload failed and we're not continuing on errors, stop
      if (!result.success && options.onError) {
        break;
      }
    }
    
    return results;
  }
}

// Export singleton instance
export const uploadService = new UploadService();

// Export convenience functions
export const uploadFile = (file: File, metadata: AdMetadata, options?: UploadOptions) =>
  uploadService.uploadFile(file, metadata, options);

export const validateFile = (file: File) => uploadService.validateFile(file);

export const formatFileSize = (bytes: number) => uploadService.formatFileSize(bytes);

export const getSupportedFileTypes = () => uploadService.getSupportedFileTypes();

export const extractVideoMetadata = (file: File) => uploadService.extractVideoMetadata(file);

export default uploadService; 